/**
 * The party call log's wire layer — overnight v20 §D2, the web surface over
 * `/countries/:cc/party-calls`.
 *
 * ===========================================================================
 * TRANSCRIBED FROM THE SERVICE, NOT GUESSED
 * ===========================================================================
 * `PartyCallRow` is copied field-for-field from `PartyCallDto` in
 * `apps/api/src/modules/party-calls/party-call-dto.ts`. Dates are `Date` on the server
 * and STRINGS on the wire. `party_calls.outcome` is the API entity's closed six and
 * `party-calls.test.tsx` asserts this list against that source file, so a member added
 * there without a decision here reddens a test rather than rendering a raw token.
 *
 * ===========================================================================
 * NOTHING HERE IS MASKABLE, AND THAT IS A STRUCTURAL FACT WORTH STATING
 * ===========================================================================
 * Rule 14 governs money and position. This surface carries NEITHER: `party_calls` has
 * no amount column and no coordinate column (migration 56), so no field on this type
 * can arrive as `'•••'` and no screen below needs a `classify*` helper. The one thing
 * that IS sensitive is the call `note` — operator prose — and the server's answer to
 * that is the guard, not a mask: a caller without `parties.view` in the country never
 * reaches the route at all. The note is therefore ABSENT from the events feed (the
 * channel that cannot mask per caller) and PRESENT in full here.
 *
 * ⚠ IF A MONEY FIELD IS EVER ADDED to `party_calls`, this file is where the omission
 * stops being free: the type would need the `MaskedMoney` treatment the quotes surface
 * uses, and `party-calls.test.tsx`'s money-shape scan is what will say so.
 */

import { ApiRequestError, ApiTransportError, apiRequest } from '../../lib/api';
import type { CountryCode } from '../../domain/countries';

/**
 * `party_calls.outcome` — the closed six migration 56 declares, in ENTITY ORDER.
 *
 * `'unspecified'` is first THERE for a MySQL enum-default reason (a NOT NULL enum with
 * no DEFAULT silently stores its first member, so the first member must claim nothing).
 * The filter chips below reorder for a reader, because a chip row is not an INSERT
 * statement — the same split the visit purposes make.
 */
export const PARTY_CALL_OUTCOMES = [
  'unspecified',
  'no_answer',
  'connected',
  'callback_requested',
  'resolved',
  'escalated',
] as const;
export type PartyCallOutcome = (typeof PARTY_CALL_OUTCOMES)[number];

/**
 * The reader's words for each outcome. Held here rather than derived from the token so
 * the screen never renders a snake_case identifier at a human, and so a new member
 * cannot ship label-less: `party-calls.test.tsx` asserts this map covers the six exactly.
 */
export const OUTCOME_LABEL: Readonly<Record<PartyCallOutcome, string>> = {
  unspecified: 'Not stated',
  no_answer: 'No answer',
  connected: 'Spoke to customer',
  callback_requested: 'Callback requested',
  resolved: 'Resolved',
  escalated: 'Escalated',
};

/** One logged call — `party-call-dto.ts` (`PartyCallDto`). */
export interface PartyCallRow {
  id: string;
  country_id: number;
  party_id: string;
  party_name: string | null;
  /** Who made or took the call. NOT NULL in the column, so never null here. */
  logged_by: string;
  logged_by_name: string | null;
  outcome: PartyCallOutcome;
  /** Operator prose. Never truncated by the server; never on the events feed. */
  note: string;
  /**
   * What the customer said their Tally serial is — migration 61, v24 C1. Captured
   * UNVALIDATED by design until D-2 lands (AM-5): a claim, not a fact — so the screen
   * renders it as what was said, never as a verified licence.
   */
  tally_serial: string | null;
  /** The call this row CORRECTS (v24 C1); null on an ordinary call. */
  amends_call_id: string | null;
  /** The rows that correct THIS one — usually empty; chains are flat, corrections are not. */
  amended_by_call_ids: string[];
  /** SERVER time at receipt, ISO-8601 UTC — never a client's claim. */
  logged_at: string;
  created_at: string;
  updated_at: string;
}

export interface Paged<T> {
  data: T[];
  page: number;
  perPage: number;
  total: number;
}

export interface PartyCallFilters {
  /** One customer's history — what the party panel passes. */
  partyId?: string;
  loggedBy?: string;
  /**
   * PASSED THROUGH UNVALIDATED, deliberately — the register idiom. The server owns the
   * vocabulary and answers 400 naming it; a hand-typed `?outcome=bogus` silently dropped
   * would render the WHOLE register under a URL claiming to be filtered.
   */
  outcome?: string;
  dateFrom?: string;
  dateTo?: string;
  page: number;
  perPage: number;
}

/**
 * ⚠ THERE IS NO `country` PARAMETER IN THIS QUERY AND THERE MUST NEVER BE ONE. The
 * country is a PATH segment the server's guard validates against the token. A filter a
 * client could set would be a second, weaker copy of that decision.
 */
export async function fetchPartyCalls(
  countryCode: CountryCode,
  filters: PartyCallFilters,
  signal?: AbortSignal,
): Promise<Paged<PartyCallRow>> {
  return apiRequest<Paged<PartyCallRow>>(`/countries/${countryCode}/party-calls`, {
    query: {
      party_id: filters.partyId,
      logged_by: filters.loggedBy,
      outcome: filters.outcome,
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

export const partyCallKeys = {
  register: (countryCode: CountryCode, filters: PartyCallFilters) =>
    ['party-calls', countryCode, filters] as const,
};

/**
 * LOG A CALL — `POST /countries/:cc/party-calls` (v24 C1 gives the body its serial).
 *
 * ⚠ BLANK MEANS ABSENT, for both optional fields. The API DTO runs
 * `forbidNonWhitelisted` semantics on VALUES too: a `tally_serial: ''` would record
 * that the question was asked and answered with nothing — which is not what an empty
 * box means (the `InquiryCreatePage` rule, verbatim in shape). Same for `outcome`:
 * omitted records `'unspecified'` — "nobody said" — which is true.
 */
export interface LogPartyCallBody {
  party_id: string;
  outcome?: PartyCallOutcome;
  note: string;
  tally_serial?: string;
}

export async function logPartyCall(
  countryCode: CountryCode,
  body: LogPartyCallBody,
): Promise<PartyCallRow> {
  return apiRequest<PartyCallRow>(`/countries/${countryCode}/party-calls`, {
    method: 'POST',
    body,
  });
}

/**
 * AMEND A CALL — `POST /parties/:id/calls/:callId/amend` (v24 C1). Appends a NEW row
 * pointing at the one it corrects; the original is byte-unchanged. The row being
 * corrected is named in the PATH, never in the body — a payload id would be a second,
 * weaker copy of that decision. Chains are flat: the server refuses amending an
 * amendment, and the screen does not offer it.
 */
export interface AmendPartyCallBody {
  outcome?: PartyCallOutcome;
  note: string;
  tally_serial?: string;
}

export async function amendPartyCall(
  partyId: string,
  callId: string,
  body: AmendPartyCallBody,
): Promise<PartyCallRow> {
  return apiRequest<PartyCallRow>(`/parties/${partyId}/calls/${callId}/amend`, {
    method: 'POST',
    body,
  });
}

/**
 * One sentence for a failed read. The API envelope's message names the actual refusal;
 * a transport failure gets the generic sentence, because "check your connection" is
 * wrong advice for a 403.
 */
export function partyCallErrorText(error: unknown): string {
  if (error instanceof ApiRequestError && error.message !== '') return error.message;
  if (error instanceof ApiTransportError) return error.message;
  return 'The request failed. Check your connection and try again.';
}
